import React, { useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import logo from "../assets/filepigeon_logo.png";
import AiSpendBadge from "./AiSpendBadge";
import { clearEngagementState, CLEAR_ON_SIGNOUT_PROMPT } from "../utils/clearEngagementState";
import { DEMO_VIEW_AVAILABLE, setDemoView, useDemoView } from "../theme/demoView";

type Project = { id: string; name: string; folderLabel?: string; updatedAt: string };
const LS_KEY = "projects_v1";

function useCurrentProjectName(pathname: string) {
  // Extract /project/:id without depending on useParams
  const match = pathname.match(/\/project\/([^/]+)/);
  const id = match?.[1];
  return useMemo(() => {
    if (!id) return "";
    try {
      const raw = localStorage.getItem(LS_KEY);
      const list: Project[] = raw ? JSON.parse(raw) : [];
      return list.find((p) => p.id === id)?.name ?? "";
    } catch {
      return "";
    }
  }, [id]);
}

function getInitials(name?: string, email?: string) {
  if (name && name.trim()) {
    const parts = name.trim().split(/\s+/);
    return (parts[0][0] + (parts[1]?.[0] ?? "")).toUpperCase();
  }
  if (email) return email.slice(0, 2).toUpperCase();
  return "ME";
}

export default function Header() {
  const location = useLocation();
  const projectName = useCurrentProjectName(location.pathname);

  const { accounts, instance } = useMsal();
  const account = accounts[0];
  const displayName = account?.name;
  const email = account?.username;

  const [open, setOpen] = useState(false);
  const lbView = useDemoView();

  const logout = () => {
    // Clear this browser's engagement data before signing out. Tokens live in
    // sessionStorage and go with logoutRedirect, but project content sits in
    // localStorage/IndexedDB and is not namespaced by user — on a shared
    // machine the next person to sign in would otherwise see it.
    if (!window.confirm(CLEAR_ON_SIGNOUT_PROMPT)) return;
    clearEngagementState();
    // Uses the postLogoutRedirectUri from your MSAL config, or falls back to origin
    instance.logoutRedirect().catch(() => {
      // no-op fallback
    });
  };

  return (
    <header
      style={{
        position: "sticky",
        top: 0,
        zIndex: 20,
        background: "#ffffff",
        borderBottom: "1px solid var(--theme-tint-20)",
        boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
      }}
    >
      <div
        style={{
          height: 56,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 16px",
          fontFamily: "Arial",
        }}
      >
        {/* Left: brand + breadcrumb (the Leo Berwick view drops FilePigeon branding) */}
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          {!lbView && (
            <Link to="/home" style={{ display: "flex", alignItems: "center", gap: 8, textDecoration: "none" }}>
              <img
                src={logo}
                alt="FilePigeon"
                style={{ height: 36, width: 36, objectFit: "contain" }}
              />
              <span style={{ fontWeight: 800, color: "var(--theme-ink)", fontSize: 18, letterSpacing: 0.2 }}>
                FilePigeon
              </span>
            </Link>
          )}

          <nav
            aria-label="breadcrumb"
            style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--theme-ink)" }}
          >
            <Link to="/home" style={{ color: "var(--theme-ink)", textDecoration: "none", fontWeight: 700 }}>
              Home
            </Link>
            {!lbView && (
              <>
                <span style={{ color: "var(--theme-divider)" }}>|</span>
                <Link to="/why" style={{ color: "var(--theme-ink)", textDecoration: "none", fontWeight: 700 }}>
                  Why FilePigeon?
                </Link>
              </>
            )}
            {projectName && (
              <>
                <span style={{ color: "var(--theme-divider)" }}>|</span>
                <span style={{ fontWeight: 700 }}>{projectName}</span>
              </>
            )}
          </nav>
        </div>

        {/* Right: AI spend badge + account chip (MS style) */}
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <AiSpendBadge />
          <div style={{ position: "relative" }}>
            <button
              onClick={() => setOpen((v) => !v)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              background: "transparent",
              border: "1px solid var(--theme-tint-23)",
              borderRadius: 999,
              padding: "6px 10px",
              cursor: "pointer",
            }}
            title={email || "Signed in"}
          >
            <div
              aria-hidden
              style={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                background: "var(--theme-accent)",
                color: "#fff",
                display: "grid",
                placeItems: "center",
                fontWeight: 800,
                fontSize: 12,
              }}
            >
              {getInitials(displayName, email)}
            </div>
            <div style={{ textAlign: "left" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "var(--theme-ink)", lineHeight: 1 }}>
                {displayName || "Signed in"}
              </div>
              <div style={{ fontSize: 11, color: "var(--theme-ink-soft)", lineHeight: 1, marginTop: 2 }}>
                {email || ""}
              </div>
            </div>
            {/* CSS properties rather than SVG attributes: attributes don't resolve var() everywhere */}
            <svg width="16" height="16" viewBox="0 0 20 20" style={{ fill: "var(--theme-ink-soft)" }} aria-hidden>
              <path d="M5.5 7.5l4.5 4.5 4.5-4.5" style={{ stroke: "var(--theme-ink-soft)" }} strokeWidth="2" fill="none" />
            </svg>
          </button>

          {open && (
            <div
              role="menu"
              style={{
                position: "absolute",
                right: 0,
                marginTop: 8,
                background: "#fff",
                border: "1px solid var(--theme-tint-20)",
                borderRadius: 10,
                boxShadow: "0 8px 20px rgba(0,0,0,0.08)",
                minWidth: 220,
                padding: 8,
                zIndex: 30,
              }}
            >
              <div style={{ padding: "8px 10px", fontSize: 12, color: "var(--theme-ink-soft)" }}>
                Signed in as <b>{displayName || email || "User"}</b>
              </div>
              <hr style={{ border: "none", borderTop: "1px solid var(--theme-tint-12)", margin: "6px 0" }} />
              {DEMO_VIEW_AVAILABLE && (
                <button
                  role="menuitemcheckbox"
                  aria-checked={lbView}
                  onClick={() => setDemoView(!lbView)}
                  style={{
                    width: "100%",
                    background: lbView ? "var(--theme-tint-7)" : "#ffffff",
                    color: "var(--theme-ink)",
                    border: "1px solid var(--theme-tint-23)",
                    borderRadius: 8,
                    padding: "8px 10px",
                    marginBottom: 6,
                    cursor: "pointer",
                    fontWeight: 700,
                    fontSize: 12,
                  }}
                >
                  Leo Berwick view: {lbView ? "On" : "Off"}
                </button>
              )}
              <button
                onClick={logout}
                style={{
                  width: "100%",
                  background: "#ffffff",
                  color: "var(--theme-ink)",
                  border: "1px solid var(--theme-tint-23)",
                  borderRadius: 8,
                  padding: "8px 10px",
                  cursor: "pointer",
                  fontWeight: 700,
                  fontSize: 12,
                }}
              >
                Sign out
              </button>
            </div>
          )}
          </div>
        </div>
      </div>
    </header>
  );
}
